'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface TopbarProps {
  sidebarCollapsed: boolean;
  onSidebarToggle: () => void;
}

export default function Topbar({ sidebarCollapsed, onSidebarToggle }: TopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  return (
    <header
      className="fixed top-0 right-0 z-30 flex items-center h-16 px-4 gap-3"
      style={{
        left: sidebarCollapsed ? '64px' : '240px',
        background: 'rgba(10, 10, 15, 0.85)',
        borderBottom: '1px solid var(--border)',
        backdropFilter: 'blur(12px)',
        transition: 'left 300ms ease',
      }}
    >
      {/* Mobile sidebar toggle */}
      <button
        onClick={onSidebarToggle}
        className="btn-ghost p-2 lg:hidden"
        aria-label="Toggle sidebar"
      >
        <Icon name="Bars3Icon" size={20} />
      </button>

      {/* Page context breadcrumb */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-sm hidden sm:block" style={{ color: 'var(--muted-foreground)' }}>
          Artist Portal
        </span>
        <Icon name="ChevronRightIcon" size={14} className="text-muted-foreground hidden sm:block" />
        <span className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
          Dashboard
        </span>
      </div>

      {/* Search */}
      <div className="relative hidden md:flex items-center">
        {searchOpen ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search releases, tracks, UPC..."
              className="input-field w-72 py-2 text-sm"
              autoFocus
              onBlur={() => { if (!searchValue) setSearchOpen(false); }}
            />
            <button
              onClick={() => { setSearchOpen(false); setSearchValue(''); }}
              className="btn-ghost p-1.5"
            >
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setSearchOpen(true)}
            className="btn-ghost flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{ border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
          >
            <Icon name="MagnifyingGlassIcon" size={16} />
            <span className="text-sm">Search</span>
            <kbd
              className="text-2xs px-1.5 py-0.5 rounded font-mono"
              style={{ background: 'var(--muted)', color: 'var(--muted-foreground)', border: '1px solid var(--border-light)' }}
            >
              ⌘K
            </kbd>
          </button>
        )}
      </div>

      {/* INR Currency Badge */}
      <div
        className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold"
        style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
      >
        <span className="text-xs font-bold" style={{ color: 'var(--primary-light)' }}>₹</span>
        <span className="text-xs hidden lg:inline" style={{ color: 'var(--muted-foreground)' }}>INR</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {/* New Release CTA */}
        <Link href="/create-release" className="btn-primary py-2 px-3 text-sm hidden sm:flex">
          <Icon name="PlusIcon" size={16} />
          New Release
        </Link>

        {/* Notifications */}
        <button className="btn-ghost p-2 relative">
          <Icon name="BellIcon" size={20} />
          <span className="notification-dot" />
        </button>

        {/* Help */}
        <button className="btn-ghost p-2">
          <Icon name="QuestionMarkCircleIcon" size={20} />
        </button>
      </div>
    </header>
  );
}